import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountStatisticsData } from '@hub/features/account-dashboard/hooks';
import { formatCurrency } from '@monorepo/client-common';
import {
  type AccountStats,
  type ClosedPosition,
  FeeRebateStatus,
  type OpenPosition,
} from '@monorepo/types';
import { useDashboard } from '../../../../../../../../contexts';
import {
  StyledStatusBarContainer,
  StyledStatusBarItem,
  StyledStatusBarLabel,
  StyledStatusBarValue,
} from './trades-table-status-bar.styled';

interface StatusBarItemConfig {
  label: string;
  value: string;
}

interface TradesTableStatusBarProps {
  positions: (ClosedPosition | OpenPosition)[];
  totalCount: number;
}

const formatCurrencyValue = (value?: number): string =>
  formatCurrency(value ?? 0, { currency: 'USD', decimalPlaces: 2 });

interface PositionDerivedStats {
  totalTrades: number;
  profitTrades: number;
  totalFees: number;
  totalNetProfit: number;
  paidFees: number;
}

const isClosedPosition = (
  position: ClosedPosition | OpenPosition,
): position is ClosedPosition => {
  return 'isClosed' in position;
};

const computeStatsFromPositions = (
  positions: (ClosedPosition | OpenPosition)[],
  totalCount: number,
): PositionDerivedStats => {
  let totalFees = 0;
  let totalNetProfit = 0;
  let profitTrades = 0;
  let paidFees = 0;

  for (const position of positions) {
    const fee = position.fee || 0;
    totalFees += fee;
    totalNetProfit += position.profitAndLoss || 0;
    if (position.profitAndLoss > 0) {
      profitTrades++;
    }

    // Paid fees = fees from positions where rebate is NOT done
    if (
      isClosedPosition(position) &&
      position.feeRebate?.status !== FeeRebateStatus.Done
    ) {
      paidFees += fee;
    }
  }

  return {
    totalTrades: totalCount,
    profitTrades,
    totalFees,
    totalNetProfit,
    paidFees,
  };
};

const resolveStats = (
  apiStats: AccountStats | undefined,
  positionStats: PositionDerivedStats,
): PositionDerivedStats => {
  if (!apiStats) return positionStats;

  const hasData =
    apiStats.totalTrades || apiStats.totalFees || apiStats.totalNetProfit;
  if (!hasData) return positionStats;

  return {
    totalTrades: apiStats.totalTrades ?? 0,
    profitTrades: apiStats.profitTrades ?? 0,
    totalFees: apiStats.totalFees ?? 0,
    totalNetProfit: apiStats.totalNetProfit ?? 0,
    paidFees: apiStats.totalRebatedFees ?? 0,
  };
};

const buildStatusBarItems = (
  stats: AccountStats | undefined,
  positionStats: PositionDerivedStats,
  isFeeRebateEligible: boolean,
  t: (key: string, defaultValue?: string) => string,
): StatusBarItemConfig[] => {
  const { totalTrades, profitTrades, totalFees, totalNetProfit, paidFees } =
    resolveStats(stats, positionStats);

  const items: StatusBarItemConfig[] = [
    {
      label: t('tradesTable.statusBar.trades', 'Trades'),
      value: String(totalTrades),
    },
    {
      label: t('tradesTable.statusBar.fees', 'Fees'),
      value: formatCurrencyValue(totalFees),
    },
  ];

  if (isFeeRebateEligible) {
    items.push({
      label: t('tradesTable.statusBar.paidFees', 'Paid Fees'),
      value: formatCurrencyValue(paidFees),
    });
  }

  items.push(
    {
      label: t('tradesTable.statusBar.winRate', 'Win Rate'),
      value: `${profitTrades}/${totalTrades}`,
    },
    {
      label: t('tradesTable.statusBar.totalPnl', 'Total P&L'),
      value: formatCurrencyValue(totalNetProfit),
    },
  );

  return items;
};

export const TradesTableStatusBar: FC<TradesTableStatusBarProps> = ({
  positions,
  totalCount,
}) => {
  const { t } = useTranslation('dashboard');
  const { account, program } = useDashboard();

  const accountId = account?.tsAccountId || account?._id || '';
  const isFeeRebateEligible = program?.config?.isFeeRebateEligible ?? false;

  const { data: stats } = useAccountStatisticsData(
    accountId,
    account || undefined,
  );

  const positionStats = useMemo(
    () => computeStatsFromPositions(positions, totalCount),
    [positions, totalCount],
  );

  const translate = (key: string, defaultValue?: string): string =>
    t(key, defaultValue ?? '');

  const items = useMemo(
    () =>
      buildStatusBarItems(stats, positionStats, isFeeRebateEligible, translate),
    [stats, positionStats, isFeeRebateEligible, t],
  );

  return (
    <StyledStatusBarContainer>
      {items.map((item) => (
        <StyledStatusBarItem key={item.label}>
          <StyledStatusBarLabel variant="inherit">
            {item.label}:
          </StyledStatusBarLabel>
          <StyledStatusBarValue variant="inherit">
            {item.value}
          </StyledStatusBarValue>
        </StyledStatusBarItem>
      ))}
    </StyledStatusBarContainer>
  );
};
